import React from 'react';

/**
 * CBCMetricsCard - Displays CBC (Conversational Boundary Control) metrics
 */
const CBCMetricsCard = ({ metrics, summary }) => {
  const cardStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  };

  const headerStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '8px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px'
  };

  const metricBoxStyle = {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: '6px',
    textAlign: 'center'
  };

  const metricValueStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333'
  };

  const metricLabelStyle = {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px'
  };

  const phaseStyle = (phase) => {
    const phases = {
      normal: { color: '#28a745', bg: '#d4edda' },
      observation: { color: '#17a2b8', bg: '#d1ecf1' },
      soft_reconnection: { color: '#ffc107', bg: '#fff3cd' },
      explicit_reconnection: { color: '#fd7e14', bg: '#ffe5d0' },
      alert_triggered: { color: '#dc3545', bg: '#f8d7da' }
    };
    const config = phases[phase] || phases.normal;
    return {
      display: 'inline-block',
      padding: '4px 8px',
      backgroundColor: config.bg,
      color: config.color,
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600'
    };
  };

  // Get current state metrics
  const currentState = metrics?.currentDriftState || {};
  const sessionMetrics = metrics?.sessionMetrics || {};

  // Use summary if provided (aggregated stats)
  const therapeuticRatio = summary?.therapeuticRatio ??
    (sessionMetrics.therapeuticContentRatio ? (sessionMetrics.therapeuticContentRatio * 100).toFixed(1) : null);
  const driftEpisodes = summary?.driftEpisodes ?? sessionMetrics.driftEpisodes ?? 0;
  const reconnectionAttempts = summary?.reconnectionAttempts ?? (metrics?.reconnectionHistory?.length || 0);
  const reconnectionSuccess = summary?.reconnectionSuccessRate ?? null;

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>CBC Metrics (Conversational Boundary Control)</div>

      <div style={gridStyle}>
        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>
            {therapeuticRatio !== null ? `${therapeuticRatio}%` : 'N/A'}
          </div>
          <div style={metricLabelStyle}>Therapeutic Ratio</div>
        </div>

        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>{driftEpisodes}</div>
          <div style={metricLabelStyle}>Drift Episodes</div>
        </div>

        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>{reconnectionAttempts}</div>
          <div style={metricLabelStyle}>Reconnection Attempts</div>
        </div>

        {reconnectionSuccess !== null && (
          <div style={metricBoxStyle}>
            <div style={metricValueStyle}>{reconnectionSuccess}%</div>
            <div style={metricLabelStyle}>Reconnection Success</div>
          </div>
        )}

        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>{currentState.driftScore || 0}</div>
          <div style={metricLabelStyle}>Current Drift Score</div>
        </div>

        <div style={metricBoxStyle}>
          <div style={phaseStyle(currentState.currentPhase || 'normal')}>
            {(currentState.currentPhase || 'normal').replace(/_/g, ' ')}
          </div>
          <div style={metricLabelStyle}>Current Phase</div>
        </div>
      </div>

      {sessionMetrics.totalExchanges > 0 && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
          Total Exchanges: {sessionMetrics.totalExchanges} |
          Therapeutic: {sessionMetrics.therapeuticExchanges || 0} |
          Rapport: {sessionMetrics.rapportExchanges || 0} |
          Neutral: {sessionMetrics.neutralExchanges || 0} |
          Avoidance: {sessionMetrics.avoidanceExchanges || 0}
        </div>
      )}
    </div>
  );
};

export default CBCMetricsCard;
